import {
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    Text,
    Button,
} from '@chakra-ui/react';
import { AdditionReport } from 'common/interfaces/reports';
import React from 'react';
import { usePagination, useTable } from 'react-table';
import { useReportDownload } from 'api/hooks/ApiHooks';
import { useAuthContext } from 'common/context/authContext';
import Paginator from './paginator';

type Proptypes = {
    tripId: string;
    listData?: AdditionReport[];
};

function AdditionReportTable(props: Proptypes) {
    const { listData, tripId } = props;

    const data = React.useMemo(
        () =>
            (listData || []).map(
                ({ contact: contacts, optionalAdditionName }) => {
                    // contact is array type, so we rename here for clarity
                    if (!contacts) return {};

                    if (contacts.length > 1) {
                        return {
                            name: contacts.map((contact) => (
                                <Text mb={2} key={`${contact.firstName}`}>
                                    {contact.firstName && contact.lastName
                                        ? `${contact.lastName}, ${contact.firstName}`
                                        : 'Ingen påmeldte'}
                                </Text>
                            )),
                            addition: optionalAdditionName,
                            total: contacts.length,
                        };
                    }
                    return {
                        name:
                            contacts[0]?.firstName && contacts[0]?.lastName
                                ? `${contacts[0].lastName}, ${contacts[0].firstName}`
                                : 'Ingen påmeldte',
                        addition: optionalAdditionName,
                        total: contacts.length,
                    };
                }
            ),
        [listData]
    );

    const columns = React.useMemo(
        () => [
            {
                Header: 'Påmeldte',
                accessor: 'name',
            } as const,
            {
                Header: 'Aktivitet',
                accessor: 'addition', // accessor is the "key" in the data
            } as const,
            {
                Header: 'Totalt',
                accessor: 'total',
            } as const,
        ],
        []
    );

    const { startDownload, loading } = useReportDownload(
        useAuthContext(),
        tripId,
        'Deltakerliste ink. utflukter'
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0 },
        },
        usePagination
    );
    return (
        <div>
            <Table variant='striped' {...getTableProps()} mb={4}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps()}>
                                    {column.render('Header')}
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <Tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return (
                                        <Td
                                            {...cell.getCellProps({
                                                style: {
                                                    width: cell.column.width,
                                                    maxWidth:
                                                        cell.column.maxWidth,
                                                    minWidth:
                                                        cell.column.minWidth,
                                                },
                                            })}
                                        >
                                            {cell.render('Cell')}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
            <Paginator
                canNextPage={canNextPage}
                canPreviousPage={canPreviousPage}
                currentPageIndex={pageIndex}
                gotoPage={gotoPage}
                nextPage={nextPage}
                numberOfPages={pageCount}
                previousPage={previousPage}
                pageOptions={pageOptions}
                pageSize={pageSize}
                setPageSize={setPageSize}
            />
            <Button
                float='right'
                mx={5}
                onClick={startDownload}
                isLoading={loading}
            >
                Skriv ut
            </Button>
        </div>
    );
}

export default AdditionReportTable;
